import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { ListGroup, ListWithMeta, ThemeRow, ThemeTokens } from "@listto/shared";

export interface SidebarQueryResult<T> {
  data: T[] | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

const MIN_REQUIRED_GLOWS = 3;

const isStringArray = (arr: unknown): arr is string[] =>
  Array.isArray(arr) && arr.every((item) => typeof item === "string");

const parseGlowColors = (raw: string | null | undefined): string[] | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStringArray(parsed) && parsed.length > 0) {
      return parsed;
    }
  } catch {
    // Ignore parsing errors for invalid raw JSON strings, falling back safely to null
  }
  return null;
};

export const getListNameForPath = (
  path: string,
  t: (message: string | MessageDescriptor) => string,
  listsData: ListWithMeta[] | undefined,
) => {
  if (path === "/") {
    return t(msg`Dashboard`);
  }
  if (path === "/all") {
    return t(msg`All Tasks`);
  }
  if (path === "/my-day") {
    return t(msg`My Day`);
  }
  if (path === "/important") {
    return t(msg`Important`);
  }
  if (path === "/planned") {
    return t(msg`Planned`);
  }
  if (path === "/inbox") {
    return t(msg`Inbox`);
  }

  const [, listId] = path.split("/lists/");
  if (listId) {
    const list = listsData?.find((l) => l.id === listId);
    return list?.name ?? t(msg`List`);
  }
  return t(msg`List`);
};

export const getListContextIdForPath = (path: string): string | undefined => {
  const listMatch = /\/lists\/([^/]+)/.exec(path);
  if (listMatch) {
    return listMatch[1];
  }
  if (path.includes("/important")) {
    return "important";
  }
  if (path.includes("/my-day")) {
    return "my-day";
  }
  if (path.includes("/planned")) {
    return "planned";
  }
  if (path.includes("/inbox")) {
    return "inbox";
  }
  return undefined;
};

export const getBackToRouteForPath = (path: string) => {
  if (path.includes("/lists/")) {
    return "/lists/$listId";
  }
  if (path.includes("/important")) {
    return "/important";
  }
  if (path.includes("/my-day")) {
    return "/my-day";
  }
  if (path.includes("/planned")) {
    return "/planned";
  }
  if (path.includes("/inbox")) {
    return "/inbox";
  }
  return "/all";
};

export const getGlowColors = (
  path: string,
  listTheme: ThemeRow | null,
  globalTheme: ThemeRow | null,
  globalToks: ThemeTokens | null,
) => {
  const activeTheme = listTheme ?? globalTheme;
  if (activeTheme) {
    const glowArray = parseGlowColors(activeTheme.glow_colors);
    if (glowArray && glowArray.length >= MIN_REQUIRED_GLOWS) {
      return {
        glow0: glowArray[0],
        glow1: glowArray[1],
        glow2: glowArray[2],
      };
    }
  }

  if (globalToks && globalToks.id !== "none") {
    return {
      glow0: globalToks.glow0,
      glow1: globalToks.glow1,
      glow2: globalToks.glow2,
    };
  }

  if (path.includes("/important")) {
    return {
      glow0: "rgba(239, 68, 68, 0.15)",
      glow1: "rgba(245, 158, 11, 0.15)",
      glow2: "rgba(220, 38, 38, 0.1)",
    };
  }
  if (path.includes("/my-day")) {
    return {
      glow0: "rgba(245, 158, 11, 0.15)",
      glow1: "rgba(251, 191, 36, 0.15)",
      glow2: "rgba(217, 119, 6, 0.1)",
    };
  }
  if (path.includes("/planned")) {
    return {
      glow0: "rgba(16, 185, 129, 0.15)",
      glow1: "rgba(6, 182, 212, 0.15)",
      glow2: "rgba(5, 150, 105, 0.1)",
    };
  }
  if (path.includes("/inbox")) {
    return {
      glow0: "rgba(59, 130, 246, 0.15)",
      glow1: "rgba(99, 102, 241, 0.15)",
      glow2: "rgba(37, 99, 235, 0.1)",
    };
  }
  if (path.includes("/lists/")) {
    return {
      glow0: "rgba(139, 92, 246, 0.15)",
      glow1: "rgba(217, 70, 239, 0.15)",
      glow2: "rgba(124, 58, 237, 0.1)",
    };
  }
  return { glow0: "var(--glow-0)", glow1: "var(--glow-1)", glow2: "var(--glow-2)" };
};

export const getOrderedListPaths = (
  listsData: ListWithMeta[] | undefined,
  listGroupsData: ListGroup[] | undefined,
) => {
  const paths = ["/", "/all", "/my-day", "/important", "/planned", "/inbox"];

  const rawLists = listsData ?? [];
  const rawGroups = listGroupsData ?? [];

  // Order custom lists exactly like the dashboard:
  // First, for each group, add its lists
  for (const group of rawGroups) {
    const groupLists = rawLists.filter((l) => l.group_id === group.id);
    for (const list of groupLists) {
      paths.push(`/lists/${list.id}`);
    }
  }

  // Then, add ungrouped lists
  const ungrouped = rawLists.filter((l) => !l.group_id);
  for (const list of ungrouped) {
    paths.push(`/lists/${list.id}`);
  }

  return paths;
};
